import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, MessageCircle, Package, BellRing } from 'lucide-react';
import useChatStore from '../../store/chatStore';
import useAuthStore from '../../store/authStore';
import { rawMaterialAPI } from '../../utils/api';

const fmtQty = (n) => (Math.round((n || 0) * 100) / 100).toLocaleString();

/**
 * Header bell: one place for everything that needs attention — unread chat messages and raw
 * materials under their minimum. Badge = unread messages + low-stock items.
 */
export default function NotificationBell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const me = useAuthStore(s => s.user);
  const conversations = useChatStore(s => s.conversations);
  const unread = useChatStore(s => s.unread);
  const unreadTotal = useChatStore(s => s.unreadTotal);
  const [low, setLow] = useState([]);
  const [open, setOpen] = useState(false);
  const [perm, setPerm] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
  const box = useRef(null);

  const loadLow = () => rawMaterialAPI.lowStock().then(r => setLow(r.data || [])).catch(() => {});
  useEffect(() => { loadLow(); const h = setInterval(loadLow, 120000); return () => clearInterval(h); }, []);
  useEffect(() => { if (open) loadLow(); }, [open]);
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', away); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('pointerdown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  const lang = i18n.language;
  const nameOf = (m) => (lang === 'en' && m.name_en) || (lang === 'ru' && m.name_ru) || m.name;
  const unreadConvs = conversations.filter(c => (unread[c.id] || 0) > 0);
  const total = unreadTotal + low.length;
  const preview = (m) => {
    if (!m) return '';
    if (m.deleted) return t('message_deleted');
    if (m.kind === 'voice') return '🎤 ' + t('voice_message');
    if (m.kind === 'image') return '🖼 ' + (m.body || t('photo'));
    if (m.kind === 'file') return '📎 ' + (m.file_name || t('file'));
    return m.body || '';
  };
  const askPerm = async () => { try { setPerm(await Notification.requestPermission()); } catch (e) { /* ignore */ } };

  return (
    <div className="nb" ref={box}>
      <button type="button" className={`nb-btn ${total ? 'has' : ''} ${unreadTotal ? 'chat' : ''}`} onClick={() => setOpen(o => !o)}
        aria-label={t('notifications')} aria-expanded={open}>
        <Bell size={15} />
        {total > 0 && <span className="nb-count">{total > 99 ? '99+' : total}</span>}
      </button>

      {open && (
        <div className="nb-panel" role="dialog" aria-label={t('notifications')} dir={lang === 'fa' ? 'rtl' : 'ltr'}>
          <div className="nb-head"><b>{t('notifications')}</b>{total > 0 && <span>{total}</span>}</div>

          <div className="nb-sec">
            <div className="nb-sec-title"><MessageCircle size={14} /> {t('unread_messages')}</div>
            {unreadConvs.length === 0 ? <p className="nb-empty">{t('no_unread_messages')}</p> : unreadConvs.map(c => {
              const other = c.members.find(m => m.id !== me?.id);
              return (
                <button key={c.id} type="button" className="nb-item" onClick={() => navigate('/chat?c=' + c.id)}>
                  <span className="nb-ava chat">{(c.is_group ? c.title : other?.full_name || c.title || '?').trim().slice(0, 1)}</span>
                  <span className="nb-txt"><b dir="auto">{c.title}</b><small dir="auto">{preview(c.last_message)}</small></span>
                  <span className="nb-badge">{unread[c.id]}</span>
                </button>
              );
            })}
          </div>

          <div className="nb-sec">
            <div className="nb-sec-title"><Package size={14} /> {t('low_stock')}</div>
            {low.length === 0 ? <p className="nb-empty">{t('no_low_stock')}</p> : low.slice(0, 6).map(m => (
              <button key={m.id} type="button" className="nb-item" onClick={() => navigate('/raw-materials')}>
                <span className="nb-ava low">!</span>
                <span className="nb-txt"><b>{nameOf(m)}</b>
                  <small className="nb-qty"><span>{t('stock')} <bdi>{fmtQty(m.quantity)} {m.unit}</bdi></span><span>{t('minimum')} <bdi>{fmtQty(m.threshold ?? m.low_stock_threshold)} {m.unit}</bdi></span></small></span>
              </button>
            ))}
            {low.length > 6 && <button type="button" className="nb-more" onClick={() => navigate('/raw-materials')}>+{low.length - 6}</button>}
          </div>

          {perm === 'default' && (
            <button type="button" className="nb-perm" onClick={askPerm}><BellRing size={15} /> {t('enable_notifications')}</button>
          )}
        </div>
      )}
    </div>
  );
}
